import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { SEASON_KICKOFF, isWrappedUnlocked } from "../../config/season";
import { BRAND } from "../../config/theme";

const C = { text: "#f2f3f5", dim: "#a8afba", caption: "#818893", green: BRAND.green, greenOnDark: BRAND.greenOnDark, hairline: "rgba(255,255,255,.10)" };

function remaining(): { d: number; h: number; m: number; s: number } | null {
  const diff = new Date(SEASON_KICKOFF).getTime() - Date.now();
  if (diff <= 0) return null;
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
}
const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

/** Locked teaser + live countdown. Calls `onUnlock` the moment kickoff passes. */
export default function WrappedCountdown({ onClose, onUnlock }: { onClose: () => void; onUnlock: () => void }) {
  const [tick, setTick] = useState(remaining());
  useEffect(() => {
    const id = setInterval(() => {
      if (isWrappedUnlocked()) { onUnlock(); return; }
      setTick(remaining());
    }, 1000);
    return () => clearInterval(id);
  }, [onUnlock]);

  const units: Array<[string, number]> = tick
    ? [["Days", tick.d], ["Hrs", tick.h], ["Min", tick.m], ["Sec", tick.s]]
    : [];

  // Show kickoff in the VIEWER's own timezone (e.g. "Sunday, Sep 6 · 8:20 PM EDT").
  const kickoff = new Date(SEASON_KICKOFF);
  const kickoffLabel = `${kickoff.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })} · ${kickoff.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZoneName: "short" })}`;

  return (
    <Box sx={{
      position: "fixed", inset: 0, zIndex: 2000,
      fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      color: C.text, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", p: "40px 30px",
      background: `radial-gradient(120% 70% at 50% 6%, #24421d 0%, rgba(36,66,29,0) 46%), linear-gradient(180deg, #14280f 0%, #070f08 100%)`,
    }}>
      <Box component="button" aria-label="Close" onClick={onClose} sx={{
        position: "absolute", top: 24, right: 20, width: 32, height: 32, borderRadius: "50%",
        background: "rgba(0,0,0,.3)", border: `1px solid ${C.hairline}`, color: "#eaf5e6", cursor: "pointer", fontSize: 16,
      }}>×</Box>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", display: "grid", placeItems: "center", background: "rgba(255,255,255,.06)", border: `1px solid ${C.hairline}`, fontSize: 22, mb: 3 }}>🔒</Box>
      <Typography sx={{ fontSize: 23, fontWeight: 800, letterSpacing: "-.01em", maxWidth: "20ch" }}>Best Ball Summer Wrapped</Typography>
      <Typography sx={{ color: C.dim, fontSize: 13.5, mt: 1.5 }}>A recap of every draft you made this summer — Underdog and DraftKings.</Typography>
      <Box sx={{ display: "flex", gap: 1.5, mt: 3.75 }}>
        {units.map(([label, n]) => (
          <Box key={label} sx={{ minWidth: 60 }}>
            <Typography sx={{ fontSize: 40, fontWeight: 800, fontVariantNumeric: "tabular-nums", letterSpacing: "-.02em" }}>{pad(n)}</Typography>
            <Typography sx={{ fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: C.caption, fontWeight: 700, mt: 0.5 }}>{label}</Typography>
          </Box>
        ))}
      </Box>
      <Typography sx={{ mt: 4.25, fontSize: 12, color: C.caption, fontWeight: 600, letterSpacing: ".04em" }}>{kickoffLabel}</Typography>
    </Box>
  );
}
